import { useTranslation } from 'react-i18next';
import { ModuleStringsProvider } from '@platform/modularity/ModuleStringsProvider';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { ErrorBanner } from '@platform/ui';
import type { ICustomizeModuleBodyProps } from './ICustomizeModuleBodyProps';
import { SettingsFormPage } from './settings/SettingsFormPage';
import { SettingsLoadingPage } from './settings/SettingsLoadingPage';
import { SettingsRecordsPage } from './settings/SettingsRecordsPage';
import { useCustomizeHead } from './settings/useCustomizeHead';
import { useScopeDefinition } from './settings/useScopeDefinition';

/**
 * Редактор форми чи «Записи й форми» модуля всередині розділу «Налаштування» (прототип v4 cust-form / cust-module без inMod): ті самі
 * сторінки, що й з меню модуля, але крихти «Простір › Налаштування › Форми › Форма «Справа»» / «… › Модулі (на рівні «Глобально» —
 * Каталог модулів) › Справи», повернення «← Форми» / «← Модулі», а «Редагувати форму» лишається в Налаштуваннях (useCustomizeHead). Тип
 * форми — `?type=`, підсвічений тип «Записів і форм» — `?focus=`; дочірня таблиця в `?focus=` (рядок «Видатки» «Встановлених модулів» —
 * окремий модуль прототипу) відкриває «Записи й форми» цієї таблиці: «… › Модулі › Видатки». На рівні «Глобально» (ADR-027) — глобальне
 * визначення: глобальна форма й глобальне визначення модуля (кількість записів у всіх просторах, без «Відкрити модуль» і «Поля»). Доки
 * визначення вантажиться (власний модуль простору без пакета) — шапка з індикатором (SettingsLoadingPage), а не порожня сторінка.
 */
export function CustomizeModuleBody({ ws, moduleKey, section, global = false }: ICustomizeModuleBodyProps) {
  const { t } = useTranslation();
  const definition = useScopeDefinition(moduleKey, global);
  const { data: module, isLoading, error } = definition;
  const strings = useModuleStrings(moduleKey);
  const head = useCustomizeHead(ws, moduleKey, section, global, module, strings);
  if (isLoading) return <SettingsLoadingPage ws={ws} moduleKey={moduleKey} section={section} global={global} definition={definition} />;
  if (error || !module || !head.rt) return <ErrorBanner error={error ?? { message: t('app.notFound') }} />;
  return (
    <ModuleStringsProvider strings={strings}>
      {head.forms
        ? <SettingsFormPage key={head.rt.key} ws={ws} module={module} rt={head.rt} name={head.formName} crumbs={head.crumbs} links={head.links} global={global} />
        : <SettingsRecordsPage ws={ws} module={module} rt={head.child} focus={head.focus} crumbs={head.crumbs} links={head.links} global={global} />}
    </ModuleStringsProvider>
  );
}
